import navConfig from "../../layouts/dashboard/NavConfig";
import { getCookie } from "../../utils/commonFuctions/cookieUtils";

export const getMenuPermissions = () => {
  const menuData = getCookie('menuData');
  if (menuData) {
    try {
      return JSON.parse(decodeURIComponent(menuData));
    } catch (error) {
      console.error('Error parsing menu permissions:', error);
      return {};
    }
  }
  return {};
};

export const hasWritePermission = (menuKey) => {
  const permissions = getMenuPermissions();
  return permissions[menuKey]?.write || false;
};

export const hasReadPermission = (menuKey) => {
  const permissions = getMenuPermissions();
  return permissions[menuKey]?.read || false;
};




/**
 * Check if user has access to a specific menu based on permissions
 * @param {string|Array} hasAccessMenu - Permission(s) required for access
 * @param {Array} userPermissions - User's available permissions
 * @returns {boolean} - Whether user has access
 */
export const hasAccess = (hasAccessMenu, userPermissions) => {
  if (!hasAccessMenu) return true; // If no permission check is defined, allow access
  
  if (Array.isArray(hasAccessMenu)) {
    // If it's an array, check if user has access to any of the permissions
    return hasAccessMenu.some(permission => userPermissions?.includes(permission));
  } else {
    // If it's a string, check if user has that specific permission
    return userPermissions?.includes(hasAccessMenu);
  }
};

/**
 * Get the first accessible route based on user permissions
 * @param {Array} userPermissions - User's available permissions
 * @returns {string} - First accessible route path
 */
export const getFirstAccessibleRoute = (userPermissions) => {
  // Iterate through navConfig to find first accessible route
  for (const navItem of navConfig) {
    // Check if the main nav item is accessible
    if (hasAccess(navItem.hasAccessMenu, userPermissions)) {
      // If it has children, check the first accessible child
      if (navItem.children && navItem.children.length > 0) {
        for (const child of navItem.children) {
          if (hasAccess(child.hasAccessMenu, userPermissions)) {
            return child.path;
          }
        }
      } else {
        // If no children or no accessible children, return the main path
        return navItem.path;
      }
    }
  }
  
  // Fallback route if no accessible routes found
  return "/dashboard";
};

/**
 * Get all accessible routes for a user
 * @param {Array} userPermissions - User's available permissions
 * @returns {Array} - Array of accessible route objects
 */
export const getAccessibleRoutes = (userPermissions) => {
  const accessibleRoutes = [];
  
  for (const navItem of navConfig) {
    if (hasAccess(navItem.hasAccessMenu, userPermissions)) {
      // Add main route
      accessibleRoutes.push({
        title: navItem.title,
        path: navItem.path,
        hasChildren: !!navItem.children
      });
      
      // Add accessible children
      if (navItem.children && navItem.children.length > 0) {
        for (const child of navItem.children) {
          if (hasAccess(child.hasAccessMenu, userPermissions)) {
            accessibleRoutes.push({
              title: child.title,
              path: child.path,
              parent: navItem.title,
              isChild: true
            });
          }
        }
      }
    }
  }
  
  return accessibleRoutes;
};

/**
 * Check if a specific route is accessible to user
 * @param {string} routePath - Route path to check
 * @param {Array} userPermissions - User's available permissions
 * @returns {boolean} - Whether route is accessible
 */
export const isRouteAccessible = (routePath, userPermissions) => {
  const flattenRoutes = (routes) => {
    let flatRoutes = [];
    
    for (const route of routes) {
      flatRoutes.push(route);
      if (route.children) {
        flatRoutes = flatRoutes.concat(route.children);
      }
    }
    
    return flatRoutes;
  };
  
  const allRoutes = flattenRoutes(navConfig);
  const targetRoute = allRoutes.find(route => route.path === routePath);
  
  if (!targetRoute) return false;
  
  return hasAccess(targetRoute.hasAccessMenu, userPermissions);
};